import React, { useState } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Button, Card, CardContent, CardHeader, Collapse, Grid, IconButton } from '@mui/material'
import { Link, useParams } from 'react-router-dom'
import { UrlProvider } from 'urls'

import Flag from 'components/_base/Flag'
import EditChangeRequest from 'components/company/EditChangeRequest'
import Diff from 'components/data-change-requests/Diff'
import LoadingView from 'components/LoadingView'
import DescriptionListTable from 'components/table/DescriptionListTable'
import { useCompany } from 'hooks/useCompany'
import {
  useAcceptDataChangeRequests,
  useDataChangeRequest,
  useRejectDataChangeRequests,
  useUpdateDataChangeRequest,
} from 'hooks/useDataChangeRequests'
import { getDataChangeRequestDiff } from 'models/data-change-request-model'
import { getEditInitialData } from 'utils/data-change-requests'
import { formatDate } from 'utils/date'
import { findLinkedInIndustryByCode } from 'utils/industries'
import { findNaceCode } from 'utils/nace'

const requestDetailsConfig = [
  {
    title: 'Id',
    field: 'id',
  },
  {
    title: 'Company UUID',
    render: ({ uuid }: any) => <Link to={UrlProvider.getUrl('company', { uuid })}>{uuid}</Link>,
  },
  {
    title: 'Customer Id',
    render: ({ customerId }: any) => (
      <Link to={UrlProvider.getUrl('customer', { id: customerId, tab: '' })}>{customerId}</Link>
    ),
  },
  {
    title: 'User Id',
    render: ({ userId }: any) => <Link to={UrlProvider.getUrl('user', { id: userId })}>{userId}</Link>,
  },
  {
    title: 'Status',
    field: 'status',
  },
  {
    title: 'Type',
    field: 'type',
  },
  {
    title: 'Created at',
    render: ({ createdAt }: any) => formatDate(createdAt),
  },
  {
    title: 'Updated at',
    render: ({ updatedAt }: any) => formatDate(updatedAt),
  },
]

const companyDataConfig = (newData: any) => [
  {
    title: 'Name',
    render: ({ name }: any) => <Diff field="name" newData={newData} value={name} />,
  },
  {
    title: 'Alias',
    field: 'alias',
  },
  {
    title: 'Number',
    render: ({ number }: any) => <Diff field="number" newData={newData} value={number} />,
  },
  {
    title: 'UUID',
    field: 'uuid',
  },
  {
    title: 'Internet Provider (ISP)',
    render: ({ isIsp }: any) => <Diff field="isIsp" newData={newData} value={isIsp} />,
  },
  {
    title: 'Website',
    render: ({ website }: any) => <Diff field="website" newData={newData} value={website} />,
  },
  {
    title: 'Country',
    render: ({ country }: any) => <Flag country={country} />,
  },
  {
    title: 'City',
    render: ({ address }: any) => <Diff field="address.city" newData={newData} value={address.city} />,
  },
  {
    title: 'Address',
    render: ({ address }: any) => <Diff field="address.address" newData={newData} value={address.address} />,
  },
  {
    title: 'State',
    render: ({ address }: any) => <Diff field="address.state" newData={newData} value={address.state} />,
  },
  {
    title: 'Zip Code',
    render: ({ address }: any) => <Diff field="address.zipCode" newData={newData} value={address.zipCode} />,
  },
  {
    title: 'Type',
    render: ({ address }: any) => <Diff field="address.type" newData={newData} value={address.type} />,
  },
  {
    title: 'Phone country',
    render: ({ address }: any) => (
      <Diff field="address.phoneNumber.country" newData={newData} value={address?.phoneNumber?.country} />
    ),
  },
  {
    title: 'Phone country code',
    render: ({ address }: any) => (
      <Diff field="address.phoneNumber.countryCode" newData={newData} value={address?.phoneNumber?.countryCode} />
    ),
  },
  {
    title: 'Phone number',
    render: ({ address }: any) => (
      <Diff field="address.phoneNumber.number" newData={newData} value={address?.phoneNumber?.number} />
    ),
  },
  {
    title: 'Nace code (Industry)',
    render: ({ naceCode }: any) => (
      <Diff
        field="naceCode"
        newData={newData}
        value={naceCode}
        valueHint={(val: any) => findNaceCode(val)?.name || ''}
      />
    ),
  },

  {
    title: 'Linkedin Industry',
    render: ({ linkedInIndustryCode }: any) => (
      <Diff
        field="linkedInIndustryCode"
        newData={newData}
        value={linkedInIndustryCode}
        valueHint={(val: any) => findLinkedInIndustryByCode(val)?.category || ''}
      />
    ),
  },

  {
    title: 'Year founded',
    field: 'foundedAt',
    render: ({ foundedAt }: any) => <Diff field="foundedAt" newData={newData} value={foundedAt} />,
  },
  {
    title: 'Description',
    field: 'description',
    render: ({ description }: any) => <Diff field="description" newData={newData} value={description} />,
  },
  {
    title: 'Financial Report year',
    field: 'financialReport.year',
    render: ({ financialReport }: any) => (
      <Diff field="financialReport.year" newData={newData} value={financialReport.year} />
    ),
  },
  {
    title: 'Financial Report currency',
    field: 'financialReport.currency',
    render: ({ financialReport }: any) => (
      <Diff field="financialReport.currency" newData={newData} value={financialReport.currency} />
    ),
  },
  {
    title: 'Financial Report revenue',
    field: 'financialReport.revenue',
    render: ({ financialReport }: any) => (
      <Diff field="financialReport.revenue" newData={newData} value={financialReport.revenue} />
    ),
  },
  {
    title: 'Tech',
    render: ({ tech }: any) => <Diff field="tech" newData={newData} value={tech} />,
  },
  {
    title: 'Employees year',
    render: ({ employees }: any) => <Diff field="employees.year" newData={newData} value={employees.year} />,
  },
  {
    title: 'Employees from',
    render: ({ employees }: any) => <Diff field="employees.from" newData={newData} value={employees.from} />,
  },
  {
    title: 'Employees to',
    render: ({ employees }: any) => <Diff field="employees.to" newData={newData} value={employees.to} />,
  },
  {
    title: 'LinkedIn Url',
    render: ({ socials }: any) => <Diff field="socials.linkedIn" newData={newData} value={socials.linkedIn} />,
  },
  {
    title: 'Twitter Url',
    render: ({ socials }: any) => <Diff field="socials.twitter" newData={newData} value={socials.twitter} />,
  },
  {
    title: 'Crunchbase Url',
    render: ({ socials }: any) => <Diff field="socials.crunchbase" newData={newData} value={socials.crunchbase} />,
  },
  {
    title: 'Facebook Url',
    render: ({ socials }: any) => <Diff field="socials.facebook" newData={newData} value={socials.facebook} />,
  },
]

type MetadataSectionProps = {
  meta: Record<string, any>
}

const MetadataSection = ({ meta }: MetadataSectionProps) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <Grid size={12}>
      <Card>
        <CardHeader
          action={
            <IconButton
              aria-label="Toggle metadata"
              onClick={() => setExpanded(prev => !prev)}
              style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
              size="large">
              <ExpandMoreIcon />
            </IconButton>
          }
          title="AI Enrichment Metadata"
        />
        <Collapse in={expanded}>
          <CardContent>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13 }}>
              {JSON.stringify(meta, null, 2)}
            </pre>
          </CardContent>
        </Collapse>
      </Card>
    </Grid>
  )
}

const DataChangeRequestDetails = () => {
  const { id } = useParams()
  const { data: changeRequest, isLoading: loading, error } = useDataChangeRequest(id)
  const { data: company, isLoading: companyLoading, error: companyError } = useCompany(changeRequest?.uuid)
  const accept = useAcceptDataChangeRequests()
  const reject = useRejectDataChangeRequests()
  const updateChangeRequest = useUpdateDataChangeRequest()
  const [editModalOpen, setEditModalOpen] = useState(false)

  const handleAccept = () => async () => {
    if (changeRequest?.uuid) {
      await accept.mutateAsync([changeRequest.id])
    }
  }

  const handleReject = () => async () => {
    if (changeRequest?.uuid) {
      await reject.mutateAsync([changeRequest.id])
    }
  }

  const editInitialValues = changeRequest?.data ? getEditInitialData(getDataChangeRequestDiff(changeRequest.data)) : {}

  const handleEditSubmit = async (serverData: any) => {
    if (!changeRequest) return

    await updateChangeRequest.mutateAsync({
      id,
      payload: {
        customer_id: changeRequest.customerId,
        user_id: changeRequest.userId,
        company_uuid: changeRequest.uuid,
        data: serverData,
        meta: changeRequest.meta,
      },
    })
    setEditModalOpen(false)
  }

  const getTitle = () => changeRequest?.id && `Change request for ${changeRequest.uuid}`

  const renderActions = () => (
    <Box sx={{ display: 'flex', gap: '10px' }}>
      {changeRequest?.status === 'pending' && (
        <Button color="primary" onClick={() => setEditModalOpen(true)} variant="outlined">
          Edit
        </Button>
      )}
      <Button
        color="primary"
        disabled={changeRequest?.status !== 'pending'}
        onClick={handleAccept()}
        variant="contained">
        Accept
      </Button>
      <Button
        color="secondary"
        disabled={changeRequest?.status !== 'pending'}
        onClick={handleReject()}
        variant="contained">
        Reject
      </Button>
    </Box>
  )

  return (
    <LoadingView
      error={
        error
          ? 'Error occurred when loading data change requests data'
          : companyError
            ? 'Error occurred when loading company data'
            : undefined
      }
      loading={loading || companyLoading}>
      <Grid container spacing={4}>
        <Grid size={12}>
          <Card>
            <CardHeader title={getTitle() || `Request change ${id} not found`} />
            <CardContent>
              <DescriptionListTable config={requestDetailsConfig} data={changeRequest} />
            </CardContent>
          </Card>
        </Grid>
        {changeRequest?.meta && <MetadataSection meta={changeRequest.meta} />}
        <Grid size={12}>
          <Card>
            <CardHeader action={renderActions()} title="Proposed update" />
            <CardContent>
              {company?.company && changeRequest?.data && (
                <DescriptionListTable
                  config={companyDataConfig(getDataChangeRequestDiff(changeRequest.data))}
                  data={company.company}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {editModalOpen && (
        <EditChangeRequest
          initialValues={editInitialValues}
          onClose={() => setEditModalOpen(false)}
          onSubmit={handleEditSubmit}
          open={editModalOpen}
        />
      )}
    </LoadingView>
  )
}

export default DataChangeRequestDetails
